/**
 * usePracticeScenarios — fetch Estudio scenarios from backend (EXK-235 Step 1b).
 *
 * Two hooks:
 *   - `usePracticeScenariosList()`  → paginated summary (catalog UI)
 *   - `usePracticeScenarioDetail(slug)` → full detail with steps+options+ui_metadata,
 *                                          mapped to legacy `ScenarioStep[]` shape
 *                                          so existing consumers (SurgeryCockpit, etc.)
 *                                          can swap import paths without prop changes.
 *
 * Feature flag: `import.meta.env.VITE_ESTUDIO_BACKEND_SCENARIOS_ENABLED === 'true'`.
 * When OFF (default), hooks return `{ enabled: false, ... }` and callers should
 * fall back to the in-memory `practiceScenarioRegistry.ts`. Switch-over plan:
 * enable in staging first, validate seed populated, then enable in prod.
 *
 * Rules of Hooks compliance: this is a real React hook (declared at top of a
 * function component / custom hook). Callers MUST NOT call it conditionally.
 * For conditional fallback, branch on `result.enabled` AFTER the call.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getJson } from '../lib/api';
import { getCurrentUser } from '../lib/auth';
import type { ScenarioStep } from '../lib/scoring/types';
import {
  backendToScenarioSteps,
  type BackendPaginatedResponse,
  type BackendScenarioDetail,
  type BackendScenarioSummary,
} from './usePracticeScenarios.adapter';

/**
 * Resolve the flag at call time so test environments can override
 * `import.meta.env.VITE_ESTUDIO_BACKEND_SCENARIOS_ENABLED` between tests
 * via `vi.stubEnv`. We must read `import.meta.env.KEY` directly — destructuring
 * `import.meta.env` first and indexing later loses the stub binding (vitest only
 * proxies the property access on the original `env` object), which was the bug
 * that silently no-op'ed `useSessionLifecycle` tests at first.
 */
function isBackendEnabled(): boolean {
  return import.meta.env.VITE_ESTUDIO_BACKEND_SCENARIOS_ENABLED === 'true';
}

/**
 * Multi-tenant queryKey factory. Embeds `orgId` so an org switch invalidates
 * cached scenario data without manual `queryClient.invalidate*` calls
 * (defense-in-depth against [REACT-004 patterns.md]). Resolved at call site
 * via `getCurrentUser()?.orgId`; `0` sentinel for unauthenticated callers
 * keeps the key well-formed during render-before-login frames.
 */
/**
 * Normalize list options into a stable shape so `{}` and `{page: undefined}`
 * hash to the same TanStack key. Without this, two callers passing
 * structurally-distinct-but-semantically-equivalent options would fire two
 * separate `GET /scenarios` requests against the backend.
 */
function normalizeListOptions(
  options: UsePracticeScenariosListOptions,
): Required<UsePracticeScenariosListOptions> {
  return {
    page: options.page ?? 1,
    limit: options.limit ?? 50,
    search: options.search ?? '',
    specialty: options.specialty ?? '',
    difficulty: options.difficulty ?? '',
  };
}

export const practiceScenariosQueryKeys = {
  root: () =>
    [
      'dominoris',
      getCurrentUser()?.orgId ?? 0,
      'practice',
      'scenarios',
    ] as const,
  list: (options: UsePracticeScenariosListOptions) =>
    [
      ...practiceScenariosQueryKeys.root(),
      'list',
      normalizeListOptions(options),
    ] as const,
  detail: (scenarioId: number | null) =>
    [...practiceScenariosQueryKeys.root(), 'detail', scenarioId] as const,
};

// ── List hook ────────────────────────────────────────────────────────────────

export interface UsePracticeScenariosListOptions {
  page?: number;
  limit?: number;
  search?: string;
  specialty?: string;
  difficulty?: string;
}

export interface UsePracticeScenariosListResult {
  /** False when feature flag is off — caller should use legacy in-memory registry. */
  enabled: boolean;
  data: BackendScenarioSummary[] | undefined;
  meta: BackendPaginatedResponse<BackendScenarioSummary>['meta'] | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
}

export function usePracticeScenariosList(
  options: UsePracticeScenariosListOptions = {},
): UsePracticeScenariosListResult {
  const enabled = isBackendEnabled();
  const search = new URLSearchParams();
  if (options.page != null) search.set('page', String(options.page));
  if (options.limit != null) search.set('limit', String(options.limit));
  if (options.search) search.set('search', options.search);
  if (options.specialty) search.set('specialty', options.specialty);
  if (options.difficulty) search.set('difficulty', options.difficulty);
  const qs = search.toString();
  const path = qs ? `/scenarios?${qs}` : '/scenarios';

  const q: UseQueryResult<BackendPaginatedResponse<BackendScenarioSummary>, unknown> =
    useQuery({
      queryKey: practiceScenariosQueryKeys.list(options),
      queryFn: () => getJson<BackendPaginatedResponse<BackendScenarioSummary>>(path),
      enabled,
      staleTime: 5 * 60 * 1000, // 5 min — scenario catalog rarely changes
    });

  return {
    enabled,
    data: q.data?.data,
    meta: q.data?.meta,
    isLoading: q.isLoading,
    isError: q.isError,
    error: q.error,
    refetch: q.refetch,
  };
}

// ── Detail hook ──────────────────────────────────────────────────────────────

export interface UsePracticeScenarioDetailResult {
  /** False when feature flag is off — caller should use legacy `resolveScenarioSteps(slug)`. */
  enabled: boolean;
  /**
   * Legacy-typed `ScenarioStep[]` derived from backend detail + ui_metadata.
   * Same shape consumers already use, so prop wiring stays unchanged.
   */
  steps: ScenarioStep[] | undefined;
  /** Full backend payload for callers that need backend-only metadata. */
  scenario: BackendScenarioDetail | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
}

/**
 * Fetch a single scenario by backend numeric `id` (not slug — backend exposes id).
 *
 * Caller typically already has the id from the list hook. If a slug → id lookup
 * is required, the legacy `practiceScenarioRegistry` continues to provide it
 * as a stop-gap; a slug-based detail endpoint is future scope (EXK-235 Step 1b
 * extension or Step 23 versioning UX).
 */
export function usePracticeScenarioDetail(
  scenarioId: number | null,
): UsePracticeScenarioDetailResult {
  const enabled = isBackendEnabled() && scenarioId != null && scenarioId > 0;
  const q: UseQueryResult<BackendScenarioDetail, unknown> = useQuery({
    queryKey: practiceScenariosQueryKeys.detail(scenarioId),
    queryFn: () => getJson<BackendScenarioDetail>(`/scenarios/${scenarioId!}`),
    enabled,
    staleTime: 5 * 60 * 1000,
  });

  // EXK-235 APP-3 — `backendToScenarioSteps` hard-fails on contract violations
  // (missing optionsMeta — see adapter E5). The throw must NOT escape into the
  // ErrorBoundary, which would surface "Algo deu errado" instead of the
  // cockpit's loading/error screen with retry. Catching here lets the cockpit
  // render the same status screen path as a real fetch error; the actionable
  // message ("Re-seed via …") is preserved on the surfaced `error`.
  let adaptedSteps: ScenarioStep[] | undefined;
  let adapterError: unknown = null;
  if (q.data) {
    try {
      adaptedSteps = backendToScenarioSteps(q.data);
    } catch (err) {
      adapterError = err;
    }
  }

  return {
    enabled,
    scenario: q.data,
    steps: adaptedSteps,
    isLoading: q.isLoading,
    isError: q.isError || adapterError !== null,
    error: q.error ?? adapterError,
    refetch: q.refetch,
  };
}
